"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * https://confluence.oci.oraclecorp.com/display/UX/Multiple+Selection+Box
 */
var React = require("react");
var react_select_1 = require("react-select");
var creatable_1 = require("react-select/creatable");
var Messages = require("../../../codegen/Messages");
var hooks_1 = require("../../../utils/hooks");
var util_1 = require("../../../utils/util");
var InputHelper_1 = require("./InputHelper");
var useEscPropagationHandler_1 = require("../../../hooks/useEscPropagationHandler");
function isMultiSelectProps(props) {
    var asMulti = props;
    return !asMulti.isSingleSelect;
}
/**
 * Helper method to filter provided options based on currently selected input values
 */
function filterBySelected(options, selected) {
    var filtered = [];
    options.forEach(function (option) {
        if (option.options && option.options.length) {
            var hasSelect = filterBySelected(option.options, selected);
            if (hasSelect.length) {
                filtered = filtered.concat(hasSelect);
            }
        }
        else {
            if (selected.includes(option.value)) {
                filtered.push(option);
            }
        }
    });
    return filtered;
}
// prevents blank option from appearing in the dropdown menu for Creatable Selection
var defaultIsValidNewOption = function (option, 
// tslint:disable-next-line:no-unused
selectValue, selectOptions) {
    return (option !== "" &&
        -1 ===
            selectOptions.findIndex(function (selectOption) { return selectOption.value === option; }));
};
// react-select is not honoring html standard input options. this function
// was required to fix that. Bug ticket filed on repo, but resolution is unknown
var isOptionDisabled = function (option) { return option.disabled; };
var mapToValues = function (options) {
    if (options === undefined || options === null) {
        return undefined;
    }
    return Array.isArray(options) ? options.map(function (o) { return o.value; }) : options.value;
};
/**
 * Wrapper of React-Select https://github.com/JedWatson/react-select
 * Currently we do not support sub-options, but it is achievable through custom option component.
 *
 * @oui-component RichSelect
 */
var RichSelect = function (props) {
    var value = props.value, defaultValue = props.defaultValue, options = props.options, placeholder = props.placeholder, placeHolderElement = props.placeHolderElement, testId = props.testId, fieldName = props.fieldName, menuList = props.menuList, creatable = props.creatable, disabled = props.disabled, isValidNewOption = props.isValidNewOption, onInputChange = props.onInputChange, onChange = props.onChange, labelledBy = props.labelledBy, labelProp = props.label, _a = props.isSearchable, isSearchable = _a === void 0 ? false : _a, _b = props.isSingleSelect, isSingleSelect = _b === void 0 ? false : _b, _c = props.isClearable, isClearable = _c === void 0 ? false : _c, _d = props.isRTL, isRTL = _d === void 0 ? false : _d, _e = props.isCloseOnSelect, isCloseOnSelect = _e === void 0 ? true : _e, rest = __rest(props, ["value", "defaultValue", "options", "placeholder", "placeHolderElement", "testId", "fieldName", "menuList", "creatable", "disabled", "isValidNewOption", "onInputChange", "onChange", "labelledBy", "label", "isSearchable", "isSingleSelect", "isClearable", "isRTL", "isCloseOnSelect"]);
    var _f = __read(React.useState([]), 2), createdOptions = _f[0], setCreatedOptions = _f[1];
    // react-select propagates ESC to parent and closes modals, this handler contains that
    var containerRef = React.createRef();
    var _g = useEscPropagationHandler_1.default(containerRef), allowEscPropagation = _g.allowEscPropagation, blockEscPropagation = _g.blockEscPropagation;
    var allOptions = options.concat(createdOptions);
    function denormalize(val) {
        return isSingleSelect ? val.length && val[0] : val;
    }
    var mappedOnChange = function (selection) {
        allowEscPropagation();
        if (onChange) {
            var selectionArray = selection === undefined || selection === null
                ? []
                : util_1.coerceArray(selection);
            var values = selectionArray.map(function (s) { return s.value; });
            onChange(denormalize(selectionArray), {
                selected: denormalize(filterBySelected(options, values)),
                created: denormalize(filterBySelected(createdOptions, values))
            });
        }
    };
    var promptTextCreator = function (inputValue) {
        var optionLabel = typeof creatable === "object" &&
            (creatable.newOptionLabel || Messages.RichSelect.newOptionLabel());
        // Need to reverse the direction/placement of parentheses depending on the input entry point
        return isRTL
            ? "(" + inputValue + " (" + optionLabel
            : inputValue + " (" + optionLabel + ")";
    };
    var CustomSelect = creatable
        ? creatable_1.default
        : react_select_1.default;
    var hideSelectedOptions = isMultiSelectProps(props)
        ? !props.multiSelectSettings ||
            props.multiSelectSettings.isRemoveSelected !== false
        : false;
    // Add custom components
    // We are not allowing exposure to the react-select 'components' prop via spreading of the parent props
    var components = __assign({}, (menuList && { MenuList: menuList }));
    return (React.createElement(InputHelper_1.default, { disabled: disabled, value: value, defaultValue: defaultValue, fieldName: fieldName, onChange: mappedOnChange, mapFormValue: mapToValues }, function (helper) {
        var valuesArray = util_1.coerceArray(helper.value);
        var describedBy = [helper.hintId, helper.errorId].join(" ").trim();
        // Prune out created options that are no longer selected
        React.useEffect(function () {
            var filtered = filterBySelected(createdOptions, valuesArray);
            if (filtered.length !== createdOptions.length) {
                setCreatedOptions(filtered);
            }
        }, [helper.value]);
        // Fire an onChange when createdOptions have changed
        var previousCreated = hooks_1.usePrevious(createdOptions, createdOptions);
        React.useEffect(function () {
            if (!util_1.arraysAreEqual(previousCreated, createdOptions)) {
                if (isSingleSelect && createdOptions.length) {
                    helper.onChange(createdOptions[0]);
                }
                else {
                    var selection = filterBySelected(options, valuesArray).concat(createdOptions);
                    helper.onChange(denormalize(selection));
                }
            }
        }, [createdOptions]);
        var onCreateOption = function (created) {
            var newOption = [
                { value: created, label: created }
            ];
            setCreatedOptions(isSingleSelect ? newOption : createdOptions.concat(newOption));
        };
        var valueOptions = denormalize(filterBySelected(allOptions, valuesArray));
        var fallbacklabel = ((typeof helper.label === "string" ? helper.label : fieldName || "") + " " + Messages.RichSelect.search()).trim();
        var label = labelProp || fallbacklabel;
        return (React.createElement("span", __assign({ "data-test-id": testId }, util_1.ariaProps({ describedBy: describedBy }), { ref: containerRef }),
            React.createElement(CustomSelect, __assign({}, rest, { id: helper.fieldId, name: helper.fieldName, options: options, isDisabled: disabled, placeholder: placeHolderElement || placeholder, isSearchable: isSearchable, isClearable: isClearable, isRtl: isRTL, isMulti: !isSingleSelect, hideSelectedOptions: hideSelectedOptions, closeMenuOnSelect: isCloseOnSelect, value: valueOptions || "", onChange: helper.onChange, onMenuOpen: blockEscPropagation, onBlur: allowEscPropagation, onInputChange: onInputChange, components: components, classNamePrefix: "rich-select", formatCreateLabel: promptTextCreator, onCreateOption: onCreateOption, isValidNewOption: isValidNewOption || defaultIsValidNewOption, isOptionDisabled: isOptionDisabled, tabSelectsValue: false }, util_1.ariaProps({ label: label, labelledBy: labelledBy })))));
    }));
};
exports.default = RichSelect;
//# sourceMappingURL=RichSelect.js.map